import React, { useState, useEffect } from "react";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  getReferralSettings,
  updateReferralSettings,
  getReferralStatistics,
  getTopReferrers,
  getActiveReferrals,
  payReferralAmount,
} from "../../../api/admin/referralManagement";
import Pagination from "../../../components/common/Pagination";
import { useTranslation } from "../../../hooks/useTranslation";

const ReferralManagement = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [topReferrersPage, setTopReferrersPage] = useState(1);
  const [activeReferralsPage, setActiveReferralsPage] = useState(1);
  const limit = 10;
  const [activeTab, setActiveTab] = useState("settings"); // "settings", "topReferrals", or "activeReferrals"

  // Form state
  const [formData, setFormData] = useState({
    referralAmount: 0,
    validityType: "unlimited",
    expirationDays: null,
    maxUsageCount: null,
    isActive: true,
  });

  // Fetch current settings
  const { data: settingsData, isLoading: loadingSettings } = useQuery({
    queryKey: ["referralSettings"],
    queryFn: getReferralSettings,
  });

  // Update form data when settings are loaded
  useEffect(() => {
    if (settingsData?.data) {
      const settings = settingsData.data;
      setFormData({
        referralAmount: settings.referralAmount || 0,
        validityType: settings.validityType || "unlimited",
        expirationDays: settings.expirationDays || null,
        maxUsageCount: settings.maxUsageCount || null,
        isActive: settings.isActive !== undefined ? settings.isActive : true,
      });
    }
  }, [settingsData]);

  // Fetch statistics overview
  const { data: statsData, isLoading: loadingStats } = useQuery({
    queryKey: ["referralStatistics"],
    queryFn: getReferralStatistics,
    enabled: activeTab !== "settings",
  });

  // Fetch top referrers
  const { data: topReferrersData, isLoading: loadingTopReferrers } = useQuery({
    queryKey: ["topReferrers", topReferrersPage],
    queryFn: () => getTopReferrers({ page: topReferrersPage, limit }),
    enabled: activeTab === "topReferrals",
    keepPreviousData: true,
  });

  // Fetch active referrals
  const { data: activeReferralsData, isLoading: loadingActiveReferrals } = useQuery({
    queryKey: ["activeReferrals", activeReferralsPage],
    queryFn: () => getActiveReferrals({ page: activeReferralsPage, limit }),
    enabled: activeTab === "activeReferrals",
    keepPreviousData: true,
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: updateReferralSettings,
    onSuccess: () => {
      toast.success(t("referralManagement").settings.messages.updateSuccess);
      queryClient.invalidateQueries(["referralSettings"]);
      queryClient.invalidateQueries(["referralStatistics"]);
      queryClient.invalidateQueries(["topReferrers"]);
      queryClient.invalidateQueries(["activeReferrals"]);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || t("referralManagement").settings.messages.updateFailed);
    },
  });

  // Pay referral amount mutation
  const payReferralMutation = useMutation({
    mutationFn: payReferralAmount,
    onSuccess: (data) => {
      toast.success(
        `₾${data?.data?.amountPaid?.toFixed(2) || "0.00"} paid successfully to ${data?.data?.userName || "user"}`
      );
      queryClient.invalidateQueries(["topReferrers"]);
      queryClient.invalidateQueries(["referralStatistics"]);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to pay referral amount");
    },
  });

  const handlePayReferral = (userId, unpaidAmount) => {
    if (!unpaidAmount || unpaidAmount <= 0) {
      toast.error("No unpaid amount to pay");
      return;
    }

    if (window.confirm(`Pay ₾${unpaidAmount.toFixed(2)} to this referrer?`)) {
      payReferralMutation.mutate({
        userId,
        amount: unpaidAmount,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? parseFloat(value) || 0 : value,
    }));

    // Reset dependent fields when validity type changes
    if (name === "validityType") {
      setFormData((prev) => ({
        ...prev,
        expirationDays: value === "timeLimited" ? prev.expirationDays : null,
        maxUsageCount: value === "usageLimited" ? prev.maxUsageCount : null,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (formData.referralAmount < 0) {
      toast.error(t("referralManagement").settings.messages.amountRequired);
      return;
    }

    if (formData.validityType === "timeLimited" && (!formData.expirationDays || formData.expirationDays < 1)) {
      toast.error(t("referralManagement").settings.messages.expirationDaysRequired);
      return;
    }

    if (formData.validityType === "usageLimited" && (!formData.maxUsageCount || formData.maxUsageCount < 1)) {
      toast.error(t("referralManagement").settings.messages.maxUsageRequired);
      return;
    }

    updateSettingsMutation.mutate(formData);
  };

  const statistics = statsData?.data?.statistics;
  const topReferrers = topReferrersData?.data?.topReferrers || [];
  const topReferrersPagination = topReferrersData?.data?.pagination || {};
  const activeReferrals = activeReferralsData?.data?.referrals || [];
  const activeReferralsPagination = activeReferralsData?.data?.pagination || {};

  return (
    <>
      <div className="flex gap-[10px] items-center">
        <div
          onClick={() => navigate(-1)}
          className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00] cursor-pointer"
        >
          <IoIosArrowRoundBack size={24} className="text-[#fff]" />
        </div>
        <h2 className="text-[22px] font-medium">{t("referralManagement").title}</h2>
      </div>

      {/* Tabs */}
      <div className="mt-[31px] flex gap-4 border-b border-[#D9D9D9]">
        <button
          onClick={() => setActiveTab("settings")}
          className={`pb-3 px-4 font-semibold ${
            activeTab === "settings"
              ? "text-[#4CAF50] border-b-2 border-[#4CAF50]"
              : "text-gray-500"
          }`}
        >
          {t("referralManagement").tabs.settings}
        </button>
        <button
          onClick={() => setActiveTab("topReferrals")}
          className={`pb-3 px-4 font-semibold ${
            activeTab === "topReferrals"
              ? "text-[#4CAF50] border-b-2 border-[#4CAF50]"
              : "text-gray-500"
          }`}
        >
          {t("referralManagement").tabs.topReferrals}
        </button>
        <button
          onClick={() => setActiveTab("activeReferrals")}
          className={`pb-3 px-4 font-semibold ${
            activeTab === "activeReferrals"
              ? "text-[#4CAF50] border-b-2 border-[#4CAF50]"
              : "text-gray-500"
          }`}
        >
          {t("referralManagement").tabs.active}
        </button>
      </div>

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div className="mt-[28px]">
          <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden py-[20px] sm:px-[30px] px-[15px] bg-white">
            {loadingSettings ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h2 className="text-base font-semibold mb-6">{t("referralManagement").settings.title}</h2>

                {/* Referral Amount */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("referralManagement").settings.referralAmount}
                  </label>
                  <input
                    type="number"
                    name="referralAmount"
                    value={formData.referralAmount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-[#D9D9D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t("referralManagement").settings.referralAmountDescription}
                  </p>
                </div>

                {/* Validity Type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("referralManagement").settings.validityType}
                  </label>
                  <select
                    name="validityType"
                    value={formData.validityType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-[#D9D9D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
                  >
                    <option value="unlimited">
                      {t("referralManagement").settings.validityOptions.unlimited}
                    </option>
                    <option value="timeLimited">
                      {t("referralManagement").settings.validityOptions.timeLimited}
                    </option>
                    <option value="usageLimited">
                      {t("referralManagement").settings.validityOptions.usageLimited}
                    </option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("referralManagement").settings.validityTypeDescription}
                  </p>
                </div>

                {/* Expiration Days (Time Limited) */}
                {formData.validityType === "timeLimited" && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("referralManagement").settings.expirationDays}
                    </label>
                    <input
                      type="number"
                      name="expirationDays"
                      value={formData.expirationDays || ""}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full px-4 py-2 border border-[#D9D9D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
                      required={formData.validityType === "timeLimited"}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t("referralManagement").settings.expirationDaysDescription}
                    </p>
                  </div>
                )}

                {/* Max Usage Count (Usage Limited) */}
                {formData.validityType === "usageLimited" && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("referralManagement").settings.maxUsageCount}
                    </label>
                    <input
                      type="number"
                      name="maxUsageCount"
                      value={formData.maxUsageCount || ""}
                      onChange={handleInputChange}
                      min="1"
                      className="w-full px-4 py-2 border border-[#D9D9D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4CAF50]"
                      required={formData.validityType === "usageLimited"}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t("referralManagement").settings.maxUsageCountDescription}
                    </p>
                  </div>
                )}

                {/* Active Status */}
                <div className="mb-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-[#4CAF50] border-gray-300 rounded focus:ring-[#4CAF50]"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {t("referralManagement").settings.isActive}
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-8">
                    {t("referralManagement").settings.isActiveDescription}
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end mt-6">
                  <button
                    type="submit"
                    disabled={updateSettingsMutation.isLoading}
                    className="px-6 py-2 bg-[#4CAF50] text-white rounded-lg hover:bg-[#45a049] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {updateSettingsMutation.isLoading
                      ? t("referralManagement").settings.saving
                      : t("referralManagement").settings.saveSettings}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Top Referrals Tab */}
      {activeTab === "topReferrals" && (
        <div className="mt-[28px]">
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="rounded-[10px] border border-[#D9D9D9] p-6 bg-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                {t("referralManagement").statistics.totalReferrals}
              </h3>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? "..." : statistics?.totalReferrals || 0}
              </p>
            </div>
            <div className="rounded-[10px] border border-[#D9D9D9] p-6 bg-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                {t("referralManagement").statistics.totalAmountDistributed}
              </h3>
              <p className="text-2xl font-bold text-gray-900">
                ₾{loadingStats ? "..." : statistics?.totalAmount?.toFixed(2) || "0.00"}
              </p>
            </div>
            <div className="rounded-[10px] border border-[#D9D9D9] p-6 bg-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                {t("referralManagement").statistics.activeReferrers}
              </h3>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? "..." : statistics?.uniqueReferrers || 0}
              </p>
            </div>
          </div>

          {/* Top Referrers Table */}
          <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden bg-white">
            <div className="p-6 border-b border-[#D9D9D9]">
              <h3 className="text-base font-semibold">{t("referralManagement").topReferrals.title}</h3>
            </div>
            {loadingTopReferrers ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : topReferrers.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {t("referralManagement").topReferrals.noData}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-[#FFFAF2]">
                      <tr>
                        <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          {t("referralManagement").topReferrals.user}
                        </th>
                        <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          {t("referralManagement").topReferrals.referralCode}
                        </th>
                        <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          {t("referralManagement").topReferrals.referrals}
                        </th>
                        <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          {t("referralManagement").topReferrals.totalEarned}
                        </th>
                        <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          Unpaid
                        </th>
                        {/* <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          Actions
                        </th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {topReferrers.map((referrer) => (
                        <tr key={referrer.userId} className="border-t border-[#E4E4E4]">
                          <td className="py-[12px] px-[16px]">
                            <div>
                              <p className="text-[17px] font-medium text-black">{referrer.name}</p>
                              <p className="text-sm text-gray-500">{referrer.email}</p>
                            </div>
                          </td>
                          <td className="py-[12px] px-[16px] text-[17px] font-mono text-black">
                            {referrer.promoCode}
                          </td>
                          <td className="py-[12px] px-[16px] text-[17px] text-black">
                            {referrer.referralCount}
                          </td>
                          <td className="py-[12px] px-[16px] text-[17px] font-semibold text-[#4CAF50]">
                            ₾{referrer.totalEarned?.toFixed(2) || "0.00"}
                          </td>
                          <td className="py-[12px] px-[16px] text-[17px] font-semibold text-[#FF9D00]">
                            ₾{referrer.unpaidAmount?.toFixed(2) || "0.00"}
                          </td>
                          {/* <td className="py-[12px] px-[16px]">
                            <button
                              onClick={() => handlePayReferral(referrer.userId, referrer.unpaidAmount)}
                              disabled={
                                !referrer.unpaidAmount ||
                                referrer.unpaidAmount <= 0 ||
                                payReferralMutation.isLoading
                              }
                              className="px-4 py-2 bg-[#4CAF50] text-white rounded-lg hover:bg-[#45a049] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                            >
                              {payReferralMutation.isLoading ? "Paying..." : "Pay"}
                            </button>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {topReferrersPagination.totalPages > 1 && (
                  <Pagination
                    page={topReferrersPagination.page || topReferrersPage}
                    totalPages={topReferrersPagination.totalPages || 1}
                    setPage={setTopReferrersPage}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Active Referrals Tab */}
      {activeTab === "activeReferrals" && (
        <div className="mt-[28px]">
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="rounded-[10px] border border-[#D9D9D9] p-6 bg-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                {t("referralManagement").statistics.totalReferrals}
              </h3>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? "..." : statistics?.totalReferrals || 0}
              </p>
            </div>
            <div className="rounded-[10px] border border-[#D9D9D9] p-6 bg-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                {t("referralManagement").statistics.totalAmountDistributed}
              </h3>
              <p className="text-2xl font-bold text-gray-900">
                ₾{loadingStats ? "..." : statistics?.totalAmount?.toFixed(2) || "0.00"}
              </p>
            </div>
            <div className="rounded-[10px] border border-[#D9D9D9] p-6 bg-white">
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                {t("referralManagement").statistics.activeReferrers}
              </h3>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? "..." : statistics?.uniqueReferrers || 0}
              </p>
            </div>
          </div>

          {/* Active Referrals Table */}
          <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden bg-white">
            <div className="p-6 border-b border-[#D9D9D9]">
              <h3 className="text-base font-semibold">{t("referralManagement").activeReferrals.title}</h3>
            </div>
            {loadingActiveReferrals ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : activeReferrals.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {t("referralManagement").activeReferrals.noData}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-[#FFFAF2]">
                      <tr>
                        <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          {t("referralManagement").activeReferrals.referrer}
                        </th>
                        <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          {t("referralManagement").activeReferrals.referredUser}
                        </th>
                        <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          {t("referralManagement").activeReferrals.amount}
                        </th>
                        <th className="text-left text-lg font-medium py-[12px] px-[16px] whitespace-nowrap">
                          {t("referralManagement").activeReferrals.date}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeReferrals.map((referral) => (
                        <tr key={referral._id} className="border-t border-[#E4E4E4]">
                          <td className="py-[12px] px-[16px]">
                            <div>
                              <p className="text-[17px] font-medium text-black">
                                {referral.referringUserId?.name || "N/A"}
                              </p>
                              <p className="text-sm text-gray-500">
                                {referral.referringUserId?.email || "N/A"}
                              </p>
                            </div>
                          </td>
                          <td className="py-[12px] px-[16px]">
                            <div>
                              <p className="text-[17px] font-medium text-black">
                                {referral.referredUserId?.name || "N/A"}
                              </p>
                              <p className="text-sm text-gray-500">
                                {referral.referredUserId?.email || "N/A"}
                              </p>
                            </div>
                          </td>
                          <td className="py-[12px] px-[16px] text-[17px] font-semibold text-[#4CAF50]">
                            ₾{referral.amount?.toFixed(2) || "0.00"}
                          </td>
                          <td className="py-[12px] px-[16px] text-[17px] text-black">
                            {new Date(referral.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {activeReferralsPagination.totalPages > 1 && (
                  <Pagination
                    page={activeReferralsPagination.page || activeReferralsPage}
                    totalPages={activeReferralsPagination.totalPages || 1}
                    setPage={setActiveReferralsPage}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReferralManagement;
